import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useKeyboardEnterListener } from 'src/hooks/useKeyboardEnterListener';
import { useAuthSubmit } from 'src/hooks/useAuthSubmit';
import { useEffect, Dispatch, SetStateAction } from 'react';

export type PasswordInput = 'password' | 'confirmPassword';

export type AuthenticationPasswordInputProps = {
  onSubmit: () => void;
  setUserCredentials: Dispatch<
    SetStateAction<{
      email: string;
      password: string;
      confirmPassword: string;
    }>
  >;
};

export const AuthenticationPasswordInput = ({
  onSubmit,
  setUserCredentials,
}: AuthenticationPasswordInputProps) => {
  const theme = useTheme();
  const {
    passwordStrengthErrors,
    errors,
    onInputChange,
    userHasTypedInPassBox,
    userCredentials,
  } = useAuthSubmit();

  useEffect(() => {
    setUserCredentials(userCredentials);
  }, [setUserCredentials, userCredentials]);

  useKeyboardEnterListener(onSubmit);

  return (
    <Stack spacing={'8px'} textAlign="center">
      <TextField
        error={errors.password}
        helperText={
          errors.password ? 'Please enter a valid, secure password!' : undefined
        }
        label="Password"
        type={'password'}
        InputLabelProps={{ shrink: true }}
        variant="outlined"
        sx={{
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgb(192, 192, 192)',
          },
        }}
        fullWidth={true}
        name={'password'}
        value={userCredentials.password}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          return onInputChange('password', event);
        }}
        autoFocus
      />
      <TextField
        error={errors.confirmPassword}
        helperText={
          errors.confirmPassword ? 'Passwords must match!' : undefined
        }
        label="Confirm Password"
        type={'password'}
        InputLabelProps={{ shrink: true }}
        variant="outlined"
        sx={{
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgb(192, 192, 192)',
          },
        }}
        fullWidth={true}
        name={'confirm-password'}
        value={userCredentials.confirmPassword}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          return onInputChange('confirmPassword', event);
        }}
      />
      <Typography variant="body2">
        <span
          style={{
            color:
              passwordStrengthErrors['too short'] ||
              passwordStrengthErrors['too long']
                ? theme.palette.error.main
                : userHasTypedInPassBox.current
                ? theme.palette.success.main
                : 'unset',
          }}
        >
          Passwords must contain 8-32 characters{' '}
        </span>
        combining{' '}
        <span
          style={{
            color: passwordStrengthErrors['needs uppercase']
              ? theme.palette.error.main
              : userHasTypedInPassBox.current
              ? theme.palette.success.main
              : 'unset',
          }}
        >
          uppercase letters
        </span>
        ,{' '}
        <span
          style={{
            color: passwordStrengthErrors['needs lowercase']
              ? theme.palette.error.main
              : userHasTypedInPassBox.current
              ? theme.palette.success.main
              : 'unset',
          }}
        >
          lowercase letters
        </span>
        ,{' '}
        <span
          style={{
            color: passwordStrengthErrors['needs number']
              ? theme.palette.error.main
              : userHasTypedInPassBox.current
              ? theme.palette.success.main
              : 'unset',
          }}
        >
          numbers
        </span>
        , and{' '}
        <span
          style={{
            color: passwordStrengthErrors['needs symbol']
              ? theme.palette.error.main
              : userHasTypedInPassBox.current
              ? theme.palette.success.main
              : 'unset',
          }}
        >
          symbols
        </span>
        .
      </Typography>
    </Stack>
  );
};
